import HistoryIcon from '@mui/icons-material/History';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * Per-entity audit-trail mini-component (PLAN §3.8).
 *
 * **Phase B = placeholder only.** The backing endpoint
 * (`GET /admin/logs?entityType=&entityId=&limit=10`) lands in Phase D;
 * gating every detail page on that dep would stall Phase B. So this
 * renders a labelled "coming soon" stub today and accepts the
 * `entityType` / `entityId` props now so detail pages can drop it in
 * place — wiring the real fetch is a one-component swap in Phase D.
 */
export interface EntityAuditTrailProps {
  /** Logical entity kind, e.g. 'order' | 'user' | 'payout'. */
  entityType: string;
  /** Entity id the trail belongs to. */
  entityId: string;
}

export const EntityAuditTrail = ({ entityType, entityId }: EntityAuditTrailProps): ReactElement => {
  const { t } = useTranslation();
  return (
    <Box
      data-testid="entity-audit-trail"
      data-entity-type={entityType}
      data-entity-id={entityId}
      sx={{
        border: '1px dashed',
        borderColor: 'divider',
        borderRadius: 2,
        p: 2,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
        <HistoryIcon fontSize="small" sx={{ color: 'text.secondary' }} />
        <Typography variant="subtitle2">{t('audit.title')}</Typography>
      </Box>
      <Typography variant="body2" color="text.secondary">
        {t('audit.comingSoon')}
      </Typography>
    </Box>
  );
};
